<script setup lang="ts">
import { AppNumberInput, AppSingleSelect, AppSvgIcon } from '@libs/ui';

import { PaxSelectorEventType } from '../PaxSelector.model';
import type { RoomPaxConfig, PaxSelectorEvents } from '../PaxSelector.model';

import {
  getChildSelectOptions,
  getSelectedChildAgeOption,
} from './PaxInRoom.helper';

interface PaxInRoomProps {
  roomIndex: number;
  roomPaxConfig: RoomPaxConfig;
  canAddMorePaxesOrRooms: boolean;
}

const props = defineProps<PaxInRoomProps>();

interface PaxInRoomEmits {
  (eventName: 'pax-event', context: PaxSelectorEvents): void;
}

const emit = defineEmits<PaxInRoomEmits>();

function handleEvent(action: PaxSelectorEvents): void {
  emit('pax-event', action);
}
</script>

<template>
  <div class="pax-in-room">
    <div class="room-header">
      {{ $t('planner.pax.rooms', 1) }} {{ props.roomIndex + 1 }}
      <AppSvgIcon
        v-if="props.roomIndex > 0"
        name="close"
        class="remove-room-button"
        size="24"
        @click="
          handleEvent({
            eventType: PaxSelectorEventType.RemoveRoom,
            roomIndex: props.roomIndex,
          })
        "
      />
    </div>

    <div class="room-paxes">
      <div class="room-adult">
        <div class="description">
          <div class="primary">{{ $t('planner.pax.adults', 2) }}</div>
          <div class="secondary">
            {{ $t('planner.pax.from_x_years', [18]) }}
          </div>
        </div>
        <AppNumberInput
          :value="props.roomPaxConfig.amountOfAdults"
          :only-stepwise-input="true"
          :disable-decrease="props.roomPaxConfig.amountOfAdults === 1"
          :disable-increase="!props.canAddMorePaxesOrRooms"
          :decrease-button-aria-label="$t('a11y.decrease_amount')"
          :amount-input-aria-label="$t('a11y.amount')"
          :increase-button-aria-label="$t('a11y.increase_amount')"
          @increase="
            handleEvent({
              eventType: PaxSelectorEventType.AdultIncrease,
              roomIndex: props.roomIndex,
            })
          "
          @decrease="
            handleEvent({
              eventType: PaxSelectorEventType.AdultDecrease,
              roomIndex: props.roomIndex,
            })
          "
        />
      </div>

      <div class="room-child">
        <div class="description">
          <div class="primary">{{ $t('planner.pax.children', 2) }}</div>
          <div class="secondary">
            0-17 {{ $t('planner.pax.years', 2).toLowerCase() }}
          </div>
        </div>
        <AppNumberInput
          :value="props.roomPaxConfig.amountOfChildren"
          :only-stepwise-input="true"
          :disable-decrease="props.roomPaxConfig.amountOfChildren === 0"
          :decrease-button-aria-label="$t('a11y.decrease_amount')"
          :disable-increase="!props.canAddMorePaxesOrRooms"
          :amount-input-aria-label="$t('a11y.amount')"
          :increase-button-aria-label="$t('a11y.increase_amount')"
          @increase="
            handleEvent({
              eventType: PaxSelectorEventType.ChildIncrease,
              roomIndex: props.roomIndex,
            })
          "
          @decrease="
            handleEvent({
              eventType: PaxSelectorEventType.ChildDecrease,
              roomIndex: props.roomIndex,
            })
          "
        />
      </div>

      <template v-if="props.roomPaxConfig.childAges.length > 0">
        <div
          v-for="(childAge, childIndex) in props.roomPaxConfig.childAges"
          :key="childIndex"
          class="child-age"
        >
          <div class="description">
            <div class="primary">
              {{ $t('planner.pax.children') }} {{ childIndex + 1 }}
            </div>
          </div>

          <AppSingleSelect
            type="default"
            :options="getChildSelectOptions()"
            :selected="getSelectedChildAgeOption(childAge)"
            :use-teleport="false"
            @select="
              handleEvent({
                eventType: PaxSelectorEventType.UpdateChildAge,
                roomIndex: props.roomIndex,
                childIndex,
                age: Number($event.id),
              })
            "
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pax-in-room {
  .room-header {
    @include font-label-m-light;
    margin-bottom: var(--spacing-8);

    display: flex;
    align-items: center;
    justify-content: space-between;

    .remove-room-button {
      color: var(--color-neutral-70);
      cursor: pointer;
      @include on-hover {
        color: var(--color-neutral-70);
      }
    }
  }

  .room-paxes {
    border: 1px solid var(--color-neutral-30);
    border-radius: var(--border-radius-8);
    padding: var(--spacing-16);

    .room-adult,
    .room-child,
    .child-age {
      &:not(:last-child) {
        margin-bottom: var(--spacing-32);
      }

      display: flex;
      align-items: center;
      justify-content: space-between;

      .description {
        .primary {
          @include font-label-l-bold;
          margin-bottom: var(--spacing-8);
        }

        .secondary {
          @include font-label-m-light;
        }
      }

      .child-age-select {
        min-width: 136px;
        width: fit-content;
      }
    }
  }
}
</style>
